/**
 * Runtime initialization module.
 *
 * Handles lazy-loading of the heavy 3D runtime (BabylonJS, content, etc.)
 *
 * - App Clip: Loads immediately (auto-detected via IS_EYEJACK_APP)
 * - Web: Prewarmed on component mount for instant XR when user clicks button
 *
 * Usage:
 *
 *   configureRuntime({
 *     load: async () => {
 *       await resolveDeferredContentData();
 *       const { appReady } = await import('./main.app');
 *       await appReady;
 *     },
 *   });
 */

import { IS_EYEJACK_APP } from "@webcore/core/utils/utils";

let runtimePromise: Promise<void> | null = null;
let loadImpl: (() => Promise<void>) | null = null;

export type RuntimeConfig = {
  load: () => Promise<void>;
};

/**
 * Configure the runtime loader. Call once during app bootstrap.
 * In App Clip mode, loading starts immediately.
 */
export const configureRuntime = ({ load }: RuntimeConfig): void => {
  runtimePromise = null;
  loadImpl = load;

  // App Clip always loads immediately
  if (IS_EYEJACK_APP) {
    void ensureRuntimeReady();
  }
};

/**
 * Ensure the runtime is ready. Idempotent - safe to call multiple times.
 * On web, called during component mount to prewarm.
 */
export const ensureRuntimeReady = async (): Promise<void> => {
  if (runtimePromise) return runtimePromise;
  if (!loadImpl) {
    throw new Error("[runtime] Not configured. Call configureRuntime() during bootstrap.");
  }
  runtimePromise = loadImpl();
  try {
    await runtimePromise;
  } catch (error) {
    runtimePromise = null;
    throw error;
  }
};

/**
 * Check if runtime is ready (non-blocking).
 */
export const isRuntimeReady = (): boolean => runtimePromise !== null;
